/**
 * Business Case Service
 * 
 * Handles all business case related API calls to the DAB backend
 */

import { getDabUrl } from './api';

export interface BusinessCaseClient {
    id?: number;
    owner_email: string;
    owner_name: string;
    submission_date?: string;
    status: 'draft' | 'pending' | 'approved' | 'rejected' | 'deleted';

    // Header fields
    opportunity_name: string;
    client: string;
    proposal_coordinator: string;
    country_region?: string;
    duration_of_project?: string;
    budget?: string;
    deadline?: string;
    workstream?: string;
    attachment_filename?: string;
    attachment_size?: number;
    attachment_data?: string;  // Base64 encoded file content

    // Question fields
    strategic_case?: string;
    competitive_advantage?: string;
    corporate_experience?: string;
    prospective_team_configuration?: string;
    partnership?: string;
    commercial_case?: string;
    bidding_capacity?: string;
    other_risks?: string;
    conflict_of_interest?: string;
    learning_development?: string;
    gender_equality_social_inclusion?: string;

    // Approval fields
    approved_by?: string;
    approval_date?: string;
    rejection_reason?: string;

    created_at?: string;
    updated_at?: string;
}

export interface BusinessCaseServer {
    id?: number;
    owner_email: string;
    owner_name: string;
    submission_date?: string;
    status: string;
    opportunity_name: string;
    client: string;
    proposal_coordinator: string;
    country_region?: string;
    duration_of_project?: string;
    budget?: string;
    deadline?: string;
    workstream?: string;
    attachment_filename?: string;
    attachment_size?: number;
    attachment_data?: string;  // Base64 encoded file content
    strategic_case?: string;
    competitive_advantage?: string;
    corporate_experience?: string;
    prospective_team_configuration?: string;
    partnership?: string;
    commercial_case?: string;
    bidding_capacity?: string;
    other_risks?: string;
    conflict_of_interest?: string;
    learning_development?: string;
    gender_equality_social_inclusion?: string;
    approved_by?: string;
    approval_date?: string;
    rejection_reason?: string;
    created_at?: string;
    updated_at?: string;
}

/**
 * Convert server format to client format
 */
export function fromServerBusinessCase(server: BusinessCaseServer): BusinessCaseClient {
    return {
        ...server,
        status: server.status as 'draft' | 'pending' | 'approved' | 'rejected'
    };
}

/**
 * Convert client format to server format
 */
export function toServerBusinessCase(client: BusinessCaseClient): BusinessCaseServer {
    return {
        ...client,
        status: client.status
    };
}

/**
 * Submit a new business case
 */
export async function submitBusinessCase(businessCase: BusinessCaseClient): Promise<BusinessCaseClient> {
    console.log('Submitting business case:', businessCase);

    const response = await fetch(getDabUrl('BusinessCases'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(toServerBusinessCase(businessCase))
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to submit business case:', response.status, errorText);
        throw new Error(`Failed to submit business case: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Business case submitted successfully:', data);
    return fromServerBusinessCase(data.value[0]);
}

/**
 * Update an existing business case
 */
export async function updateBusinessCase(id: number, businessCase: Partial<BusinessCaseClient>): Promise<BusinessCaseClient> {
    console.log('Updating business case:', id, 'with data:', businessCase);
    
    const response = await fetch(getDabUrl(`BusinessCases/id/${id}`), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(businessCase)  // Send partial data directly for PATCH
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to update business case:', response.status, errorText);
        throw new Error(`Failed to update business case: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Business case updated successfully:', data);
    return fromServerBusinessCase(data.value[0]);
}

/**
 * Hard delete a business case from the database (admin only)
 */
export async function deleteBusinessCase(id: number): Promise<void> {
    const response = await fetch(getDabUrl(`BusinessCases/id/${id}`), {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        throw new Error(`Failed to delete business case: ${response.statusText}`);
    }
}

/**
 * Fetch business cases for a specific user
 */
export async function fetchMyBusinessCases(ownerEmail: string): Promise<BusinessCaseClient[]> {
    const response = await fetch(
        getDabUrl(`BusinessCases?$filter=owner_email eq '${ownerEmail}'&$orderby=submission_date desc`),
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch business cases: ${response.statusText}`);
    }

    const data = await response.json();
    return data.value.map((bc: BusinessCaseServer) => fromServerBusinessCase(bc));
}

/**
 * Fetch all business cases (admin only)
 */
export async function fetchAllBusinessCases(status?: 'draft' | 'pending' | 'approved' | 'rejected'): Promise<BusinessCaseClient[]> {
    let queryParams = '$orderby=submission_date desc';

    if (status) {
        queryParams += `&$filter=status eq '${status}'`;
    }

    const response = await fetch(getDabUrl(`BusinessCases?${queryParams}`), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch all business cases: ${response.statusText}`);
    }

    const data = await response.json();
    return data.value.map((bc: BusinessCaseServer) => fromServerBusinessCase(bc));
}

/**
 * Fetch a single business case by ID
 */
export async function fetchBusinessCaseById(id: number): Promise<BusinessCaseClient | null> {
    const url = getDabUrl(`BusinessCases/id/${id}`);
    console.log('Fetching business case from URL:', url);
    
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    });

    if (!response.ok) {
        console.error('Failed to fetch business case:', response.status, response.statusText);
        throw new Error(`Failed to fetch business case: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Raw response data:', data);
    
    // DAB returns single item directly when fetching by primary key
    // But may also return in value array depending on endpoint format
    if (data.value && Array.isArray(data.value)) {
        console.log('Data is in value array format');
        return data.value.length > 0 ? fromServerBusinessCase(data.value[0]) : null;
    }
    
    // Direct object response
    console.log('Data is direct object format');
    return data ? fromServerBusinessCase(data) : null;
}

/**
 * Approve a business case (admin only)
 */
export async function approveBusinessCase(id: number, approvedBy: string): Promise<BusinessCaseClient> {
    return await updateBusinessCase(id, {
        status: 'approved',
        approved_by: approvedBy,
        approval_date: new Date().toISOString()
    });
}

/**
 * Copy business case attachment to SharePoint on approval
 * Uploads the file to: 01. Business Development/Business Case Forms
 * with filename format: DD_MM_YYYY_Client_StaffName.ext
 */
export async function copyAttachmentToSharePoint(businessCase: BusinessCaseClient): Promise<{ success: boolean; error?: string }> {
    try {
        console.log('Processing business case approval files. Attachment:', businessCase.attachment_filename || 'None');

        // Only send metadata - the Azure Function fetches attachment data directly from the database
        // to avoid SWA proxy request body size limits
        const response = await fetch('/api/BusinessCaseApprovalFunction', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                businessCaseId: businessCase.id,
                ownerName: businessCase.owner_name,
                client: businessCase.client,
                approvalDate: businessCase.approval_date || new Date().toISOString()
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Failed to copy attachment to SharePoint:', response.status, errorText);
            return { success: false, error: `Failed to copy attachment: ${response.statusText}` };
        }

        const result = await response.json();
        console.log('SharePoint upload result:', result);
        return { success: true };

    } catch (error) {
        console.error('Error copying attachment to SharePoint:', error);
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
}

/**
 * Reject a business case (admin only)
 */
export async function rejectBusinessCase(id: number, rejectionReason: string): Promise<BusinessCaseClient> {
    return await updateBusinessCase(id, {
        status: 'rejected',
        rejection_reason: rejectionReason
    });
}

/**
 * Save draft business case
 * Now saves as a business case with status='draft' instead of using BusinessCaseDrafts table
 * @param existingId - Optional ID of an existing case being edited (e.g., rejected case being resubmitted)
 */
export async function saveDraftBusinessCase(ownerEmail: string, ownerName: string, draftData: any, existingId?: number): Promise<BusinessCaseClient | null> {
    // If editing an existing case (e.g., rejected case), update it directly
    if (existingId) {
        console.log('Updating existing case as draft:', existingId);
    }
    
    // Check if draft already exists (only if not editing a specific case)
    const existingDrafts = await fetchMyBusinessCases(ownerEmail);
    const existingDraft = existingId 
        ? existingDrafts.find(bc => bc.id === existingId)
        : existingDrafts.find(bc => bc.status === 'draft');

    // Ensure required fields have at least empty strings (database constraints)
    // Remove empty/invalid optional fields to avoid database errors
    const businessCaseData: BusinessCaseClient = {
        owner_email: ownerEmail,
        owner_name: ownerName,
        status: 'draft',
        opportunity_name: draftData.opportunity_name || '',
        client: draftData.client || '',
        proposal_coordinator: draftData.proposal_coordinator || '',
        rejection_reason: undefined  // Clear rejection reason when saving as draft
    };

    // Only include optional fields if they have valid values
    if (draftData.country_region) businessCaseData.country_region = draftData.country_region;
    if (draftData.duration_of_project) businessCaseData.duration_of_project = draftData.duration_of_project;
    if (draftData.budget) businessCaseData.budget = draftData.budget;
    if (draftData.deadline && draftData.deadline.trim() !== '') businessCaseData.deadline = draftData.deadline;
    if (draftData.workstream) businessCaseData.workstream = draftData.workstream;
    if (draftData.attachment_filename) businessCaseData.attachment_filename = draftData.attachment_filename;
    if (draftData.attachment_size) businessCaseData.attachment_size = draftData.attachment_size;
    if (draftData.attachment_data) {
        console.log('=== ATTACHMENT DATA IN saveDraftBusinessCase ===');
        console.log('attachment_data length:', draftData.attachment_data.length);
        businessCaseData.attachment_data = draftData.attachment_data;
    }
    if (draftData.strategic_case) businessCaseData.strategic_case = draftData.strategic_case;
    if (draftData.competitive_advantage) businessCaseData.competitive_advantage = draftData.competitive_advantage;
    if (draftData.corporate_experience) businessCaseData.corporate_experience = draftData.corporate_experience;
    if (draftData.prospective_team_configuration) businessCaseData.prospective_team_configuration = draftData.prospective_team_configuration;
    if (draftData.partnership) businessCaseData.partnership = draftData.partnership;
    if (draftData.commercial_case) businessCaseData.commercial_case = draftData.commercial_case;
    if (draftData.bidding_capacity) businessCaseData.bidding_capacity = draftData.bidding_capacity;
    if (draftData.other_risks) businessCaseData.other_risks = draftData.other_risks;
    if (draftData.conflict_of_interest) businessCaseData.conflict_of_interest = draftData.conflict_of_interest;
    if (draftData.learning_development) businessCaseData.learning_development = draftData.learning_development;
    if (draftData.gender_equality_social_inclusion) businessCaseData.gender_equality_social_inclusion = draftData.gender_equality_social_inclusion;

    if (existingDraft && existingDraft.id) {
        // Update existing draft
        console.log('Updating existing draft:', existingDraft.id);
        return await updateBusinessCase(existingDraft.id, businessCaseData);
    } else {
        // Create new draft
        console.log('Creating new draft for:', ownerEmail);
        return await submitBusinessCase(businessCaseData);
    }
}

/**
 * Fetch draft business case for a user
 * Now fetches from BusinessCases table with status='draft'
 */
export async function fetchDraftBusinessCase(ownerEmail: string): Promise<any | null> {
    const cases = await fetchMyBusinessCases(ownerEmail);
    const draft = cases.find(bc => bc.status === 'draft');
    return draft || null;
}

/**
 * Delete draft business case
 * Now deletes from BusinessCases table
 */
export async function deleteDraftBusinessCase(ownerEmail: string): Promise<void> {
    const cases = await fetchMyBusinessCases(ownerEmail);
    const draft = cases.find(bc => bc.status === 'draft');

    if (draft && draft.id) {
        await updateBusinessCase(draft.id, { status: 'deleted' } as any);
    }
}
